import React, { useEffect, useMemo } from 'react';
import { observer, useComputed } from '@legendapp/state/react';
import { adviceActions, adviceState } from '@/lib/store/advice.store';
import { MessageContent } from './MessageContent';

/**
 * Inner component logic for Message
 */
const MessageComponent = ({
  advice,
  isLast,
}: {
  advice: any;
  isLast: boolean;
}) => {
  // Hooks are now called directly within this standard function component body
  const computedState = useComputed(() => {
    return {
      status: adviceState.status.get(),
      isStreaming: adviceState.isStreaming.get(),
      isChatLoading: adviceState.isChatLoading.get(),
      currentStreamingId: adviceState.streamingAdviceId.get(),
      hasAdviceContent: advice.advice && advice.advice.trim() !== '',
    };
  });

  const {
    status,
    hasAdviceContent,
    isStreaming,
    isChatLoading,
    currentStreamingId,
  } = computedState.get();

  const shouldShowLoading = useMemo(() => {
    if (!isLast) return false;
    if (status === 'ready') return false;
    if (advice.status === 'ready') return false;
    return (
      status === 'submitted' ||
      isChatLoading ||
      (advice.isNew === true && !hasAdviceContent)
    );
  }, [advice, hasAdviceContent, status, isLast, isChatLoading]);

  return (
    <div
      className='flex w-full flex-col space-y-2 py-1 sm:space-y-3 sm:py-2 md:space-y-4'
      data-testid='message-wrapper'
      data-advice-id={advice.id}
      data-is-last={isLast ? 'true' : 'false'}
      data-status={advice.status}
      data-store-status={status}
      data-is-streaming={isStreaming ? 'true' : 'false'}
      data-streaming-id={currentStreamingId || 'none'}
      data-has-content={hasAdviceContent ? 'true' : 'false'}
      data-show-loading={shouldShowLoading ? 'true' : 'false'}
      data-is-new={advice.isNew ? 'true' : 'false'}
      data-is-chat-loading={isChatLoading ? 'true' : 'false'}
    >
      <div
        key={advice.id}
        className='w-full px-0.5 sm:px-1 md:px-2'
        data-testid='chat-message'
        data-message-id={advice.id}
      >
        <MessageContent
          advice={advice}
          shouldShowLoading={shouldShowLoading}
          isLastAdvice={isLast}
          hasAdviceContent={hasAdviceContent}
        />
      </div>
    </div>
  );
};

/**
 * Message component that displays chat messages and handles loading states
 * Wrapped with observer and memo for performance and reactivity
 */
export const Message = React.memo(observer(MessageComponent));

Message.displayName = 'Message'; // Add display name for better debugging
